import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SimpleModal } from '@livable/saas-react-components';
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { track } from 'src/configs/amplitude';
import { exportableColumns } from 'src/pages/AdminPage/BillQueue/queue';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import { AUTO_HEIGHT_SCROLLBAR_ROOM } from 'src/utils/dataGrid';
import { succeeded } from 'src/components/property/UtilityPortalCredentials/asyncResult';
import { CredentialForm } from 'src/components/property/UtilityPortalCredentials/CredentialForm';
import { credentialFailureMessage, credentialStatusLabel, credentialStatusTooltip, credentialStatusVariant, isAutomatedCredential, isPausedCredential } from 'src/components/property/UtilityPortalCredentials/credentialStatus';
import { filterCredentials } from './filters';
import { LOGIN_BUCKETS, loginSummary } from './model';
import { KpiTiles, SourceChip } from './parts';

// Matches UtilityPortalCredentialService.MaxFailedSignIns: the third rejected sign-in in a row pauses the login.
const MAX_FAILED_SIGN_INS = 3;
const formatDate = value => value ? new Date(value).toLocaleDateString() : '-';
const providerFrom = filter => filter !== null && filter !== void 0 && filter.startsWith('provider:') ? filter.slice('provider:'.length).split(',').filter(Boolean) : [];
const SELECTED = '__selected';

/** A login is edited against the utility it serves; a company-wide login (no utility) only changes by import. */
export const isEditableLogin = row => !!(row !== null && row !== void 0 && row.companyId && row !== null && row !== void 0 && row.propertyUtilityId);

/**
 * Cross-company credential view for Livable staff. New logins are entered on the property's utility, where they
 * are scoped to the utility the scraper needs them for; existing ones are edited here by id. The list comes from the
 * console (`credentials`, a useAsyncList state), which also feeds the Overview inbox from the same request.
 */
export const LoginsView = observer(_ref => {
  let credentials = _ref.credentials,
    filter = _ref.filter;
  const store = useStore();
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    search = _useState2[0],
    setSearch = _useState2[1];
  const _useState3 = useState(LOGIN_BUCKETS[filter] ? filter : ''),
    _useState4 = _slicedToArray(_useState3, 2),
    bucket = _useState4[0],
    setBucket = _useState4[1];
  const _useState5 = useState(providerFrom(filter)),
    _useState6 = _slicedToArray(_useState5, 2),
    providerKeys = _useState6[0],
    setProviderKeys = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    confirming = _useState8[0],
    setConfirming = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    resuming = _useState0[0],
    setResuming = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    actionError = _useState10[0],
    setActionError = _useState10[1];
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    editingRow = _useState12[0],
    setEditing = _useState12[1];
  // Followed through reloads, so a form kept open after a save compares against what is saved now.
  const editing = editingRow ? (credentials.rows || []).find(r => r.id === editingRow.id) || editingRow : null;
  const openEdit = (row, from) => {
    setEditing(row);
    track('Bill Automation Login Edit Opened', {
      from
    });
  };
  const handleEditSaved = function () {
    let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      rotated = _ref2.rotated;
    track('Bill Automation Login Edited', {});
    credentials.reload();
    // A details-only save leaves a paused login paused: keep the form open so its notice and Resume stay in view.
    if (isPausedCredential(editing) && !rotated) return;
    setEditing(null);
  };

  // The server allows this for staff only; a pause means automation gave up, which only Livable can judge fixed.
  const handleResume = async () => {
    const model = store.admin.resumeUtilityCredentialAsync;
    setActionError('');
    setResuming(true);
    try {
      const resp = await model.run(confirming.id);
      if (!succeeded(model, resp)) {
        setActionError('Could not resume this credential.');
        return;
      }
      if ((editing === null || editing === void 0 ? void 0 : editing.id) === confirming.id) setEditing(null);
      setConfirming(null);
      track('Bill Automation Login Resumed', {});
      credentials.reload();
    } catch (ex) {
      setActionError('Could not resume this credential.');
    } finally {
      setResuming(false);
    }
  };
  const rows = credentials.rows;
  const summary = useMemo(() => loginSummary(rows), [rows]);
  const visible = useMemo(() => filterCredentials(rows, {
    search,
    bucket,
    providerKey: providerKeys
  }), [rows, search, bucket, providerKeys]);
  // From the rows themselves: a dropdown of every registry provider is a worse filter than the ones in use.
  const providerOptions = useMemo(() => [...new Set(rows.map(c => c.providerKey).filter(Boolean))].sort(), [rows]);
  const columns = useMemo(() => [
  // Customer and property first: support arrives here knowing who called, not which provider.
  {
    field: 'companyName',
    headerName: 'Customer',
    flex: 1,
    minWidth: 180,
    isExportable: true,
    renderCell: _ref3 => {
      let row = _ref3.row;
      return row.companyName || '-';
    },
    valueGetterForExport: _ref4 => {
      let row = _ref4.row;
      return row.companyName || '-';
    }
  }, {
    field: 'propertyAddress',
    headerName: 'Property',
    flex: 1.2,
    minWidth: 200,
    isExportable: true,
    renderCell: _ref5 => {
      let row = _ref5.row;
      return row.propertyAddress || '-';
    },
    valueGetterForExport: _ref6 => {
      let row = _ref6.row;
      return row.propertyAddress || '-';
    }
  }, {
    // The address the utility bills, which is not always the property's own.
    field: 'serviceAddress',
    headerName: 'Service address',
    flex: 1.2,
    minWidth: 200,
    isExportable: true,
    renderCell: _ref7 => {
      let row = _ref7.row;
      return row.serviceAddress || '-';
    },
    valueGetterForExport: _ref8 => {
      let row = _ref8.row;
      return row.serviceAddress || '-';
    }
  }, {
    field: 'providerKey',
    headerName: 'Provider',
    flex: 1,
    minWidth: 160,
    isExportable: true
  },
  // The masked hint only. The password is never returned by any endpoint this page calls.
  {
    field: 'usernameHint',
    headerName: 'Username',
    flex: 1,
    minWidth: 180,
    isExportable: true
  }, {
    field: 'accountReference',
    headerName: 'Account',
    flex: 1,
    minWidth: 140,
    isExportable: true,
    renderCell: _ref9 => {
      let row = _ref9.row;
      return row.accountReference || '-';
    },
    valueGetterForExport: _ref0 => {
      let row = _ref0.row;
      return row.accountReference || '-';
    }
  }, {
    field: 'status',
    headerName: 'Status',
    width: 200,
    isExportable: true,
    renderCell: _ref1 => {
      let row = _ref1.row;
      return /*#__PURE__*/React.createElement(Stack, {
        sx: {
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Tooltip, {
        title: credentialStatusTooltip(row, {
          isAutomated: isAutomatedCredential(row),
          staff: true
        }),
        placement: "top",
        arrow: true
      }, /*#__PURE__*/React.createElement(Chip, {
        tabIndex: 0,
        size: "small",
        label: credentialStatusLabel(row),
        variant: credentialStatusVariant(row)
      })), credentialFailureMessage(row) ? /*#__PURE__*/React.createElement(Typography, {
        variant: "caption"
      }, credentialFailureMessage(row)) : null);
    },
    valueGetterForExport: _ref10 => {
      let row = _ref10.row;
      return credentialStatusLabel(row);
    }
  }, {
    field: 'lastSucceededAt',
    headerName: 'Last success',
    width: 130,
    isExportable: true,
    renderCell: _ref11 => {
      let row = _ref11.row;
      return formatDate(row.lastSucceededAt);
    },
    valueGetterForExport: _ref12 => {
      let row = _ref12.row;
      return formatDate(row.lastSucceededAt);
    }
  }, {
    // When the company was last emailed that this login needs attention.
    field: 'lastNotifiedAt',
    headerName: 'Last emailed',
    width: 130,
    isExportable: true,
    renderCell: _ref13 => {
      let row = _ref13.row;
      return formatDate(row.lastNotifiedAt);
    },
    valueGetterForExport: _ref14 => {
      let row = _ref14.row;
      return formatDate(row.lastNotifiedAt);
    }
  }, {
    field: 'lastVerifiedAt',
    headerName: 'Last verified',
    width: 130,
    isExportable: true,
    renderCell: _ref15 => {
      let row = _ref15.row;
      return formatDate(row.lastVerifiedAt);
    },
    valueGetterForExport: _ref16 => {
      let row = _ref16.row;
      return formatDate(row.lastVerifiedAt);
    }
  }, {
    field: 'consecutiveFailedSignIns',
    headerName: 'Failed sign-ins',
    width: 130,
    isExportable: true,
    renderCell: _ref17 => {
      let row = _ref17.row;
      const n = row.consecutiveFailedSignIns || 0;
      return /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        color: n >= MAX_FAILED_SIGN_INS - 1 ? 'error' : 'inherit',
        sx: {
          fontWeight: n ? 600 : 400
        }
      }, "".concat(n, " of ").concat(MAX_FAILED_SIGN_INS));
    },
    valueGetterForExport: _ref18 => {
      let row = _ref18.row;
      return "".concat(row.consecutiveFailedSignIns || 0, " of ").concat(MAX_FAILED_SIGN_INS);
    }
  }, {
    field: 'secretVersion',
    headerName: 'Version',
    width: 90,
    type: 'number',
    isExportable: true
  }, {
    field: 'actions',
    headerName: '',
    width: 200,
    sortable: false,
    renderCell: _ref19 => {
      let row = _ref19.row;
      return /*#__PURE__*/React.createElement(Stack, {
        direction: "row",
        spacing: 6
      }, isEditableLogin(row) ? /*#__PURE__*/React.createElement(Button, {
        variant: "actionButtonInvert",
        size: "small",
        onClick: () => openEdit(row, 'button')
      }, "Edit") : /*#__PURE__*/React.createElement(Tooltip, {
        title: "This login isn't tied to a utility. Change it by import."
      }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
        variant: "actionButtonInvert",
        size: "small",
        disabled: true,
        sx: {
          opacity: 0.5
        }
      }, "Edit"))), isPausedCredential(row) ? /*#__PURE__*/React.createElement(Button, {
        variant: "actionButtonInvert",
        size: "small",
        onClick: () => setConfirming(row)
      }, "Resume") : null);
    }
  }], []);
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(KpiTiles, {
    tiles: Object.entries(LOGIN_BUCKETS).map(_ref20 => {
      let _ref21 = _slicedToArray(_ref20, 2),
        key = _ref21[0],
        label = _ref21[1];
      return {
        label,
        value: credentials.loading ? '-' : summary[key],
        onClick: () => setBucket(key)
      };
    })
  }), credentials.error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, credentials.error) : null, /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: !!confirming,
    handleClose: () => {
      setConfirming(null);
      setActionError('');
    },
    title: "Resume automatic retrieval?",
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      disabled: resuming,
      onClick: handleResume
    }, "Resume")
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "The next sweep will sign in to ", confirming === null || confirming === void 0 ? void 0 : confirming.providerKey, " with this login. If the problem that paused it is not fixed, it pauses again after 3 more unsuccessful attempts."), actionError ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      marginTop: '8px'
    }
  }, actionError) : null), /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: !!editing,
    handleClose: () => setEditing(null),
    title: "Edit login: ".concat((editing === null || editing === void 0 ? void 0 : editing.providerKey) || ''),
    subTitle: [editing === null || editing === void 0 ? void 0 : editing.companyName, editing === null || editing === void 0 ? void 0 : editing.propertyAddress].filter(Boolean).join(' · ')
  }, editing ? /*#__PURE__*/React.createElement(CredentialForm, {
    key: editing.id,
    companyId: editing.companyId,
    propertyId: editing.propertyId,
    propertyUtilityId: editing.propertyUtilityId,
    utilityTypeId: editing.utilityTypeId,
    existing: editing,
    propertyAddress: editing.propertyAddress,
    onResume: () => setConfirming(editing),
    onSaved: handleEditSaved
  }) : null), /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px',
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 8,
    useFlexGap: true,
    flexWrap: "wrap",
    sx: {
      marginBottom: '12px'
    },
    alignItems: "center"
  }, [['', 'All'], ...Object.entries(LOGIN_BUCKETS)].map(_ref22 => {
    let _ref23 = _slicedToArray(_ref22, 2),
      key = _ref23[0],
      label = _ref23[1];
    return /*#__PURE__*/React.createElement(Chip, {
      key: key || 'all',
      label: label,
      variant: bucket === key ? 'info' : 'inactive',
      onClick: () => setBucket(key),
      "aria-pressed": bucket === key
    });
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 16,
    useFlexGap: true,
    flexWrap: "wrap",
    sx: {
      marginBottom: '16px'
    },
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Search customer, property, address, provider, account",
    value: search,
    onChange: e => setSearch(e.target.value),
    sx: {
      minWidth: 340
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "Provider",
    value: providerKeys.length > 1 ? SELECTED : providerKeys[0] || '',
    onChange: e => {
      const value = e.target.value;
      if (value !== SELECTED) setProviderKeys(value ? [value] : []);
    },
    sx: {
      minWidth: 160
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "Any provider"), providerKeys.length > 1 ? /*#__PURE__*/React.createElement(MenuItem, {
    value: SELECTED
  }, "This provider (".concat(providerKeys.length, " keys)")) : null, providerOptions.map(key => /*#__PURE__*/React.createElement(MenuItem, {
    key: key,
    value: key
  }, key))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flexGrow: 1
    }
  }), /*#__PURE__*/React.createElement(SourceChip, null), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => exportCSVFromDataGrid({
      columns: exportableColumns(columns),
      rows: visible,
      name: 'utility-credentials.csv'
    })
  }, "Export CSV")), /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    sx: _objectSpread(_objectSpread({}, AUTO_HEIGHT_SCROLLBAR_ROOM), {}, {
      '& .editable-login': {
        cursor: 'pointer'
      }
    }),
    getRowClassName: _ref24 => {
      let row = _ref24.row;
      return isEditableLogin(row) ? 'editable-login' : '';
    },
    columns: columns,
    rows: visible,
    loading: credentials.loading,
    getRowId: row => row.id,
    getRowHeight: () => 'auto',
    disableRowSelectionOnClick: true,
    onRowClick: (_ref25, event) => {
      let row = _ref25.row;
      if (isEditableLogin(row) && !event.target.closest('button')) openEdit(row, 'row');
    },
    initialState: {
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25
        }
      }
    },
    pageSizeOptions: [25, 50, 100]
  }), !visible.length && !credentials.loading && !credentials.error ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginTop: '12px'
    }
  }, "No credentials stored.") : null));
});